<template lang="pug">
div.login-button-wrapper(
    v-show="show"
    v-on:mouseenter="updateMessages")

    button.login-button.btn(
        ref="loginButton"
        role="button"
        v-bind:class="busy ? 'busy disabled' : ''"
        v-bind:disabled="busy"
        v-on:click="onClick") {{ $t('user.login') }}

        slot

    dx-popover(
        v-if="!disableTooltip && popoverTarget && (showSessionExpired || showRegistrationInfo)"
        role="tooltip"
        v-bind:visible.sync="showPopover"
        v-bind:animation="animationConfig"
        v-bind:position="position"
        v-bind:target="popoverTarget"
        v-bind:wrapper-attr="{class: 'login-popover-wrapper'}")

        div.popover-message

            //- session expired
            silhouette-assistant(
                v-if="showSessionExpired"
                image-name="silhouette-1.svg"
                v-bind:bubble-nr="2"
                v-bind:reverse="true")

                div {{ $t('user.loggedOut') }}
                div {{ $t('user.loggedOutLoginPrompt') }}

                button.btn(v-on:click="loginDismiss") {{ $t('common.okUnderstood') }}

            //- registration tooltip
            dx-tooltip(
                v-bind:visible.sync="showRegistrationInfo"
                v-bind:target="popoverTarget")
                router-link(
                    v-bind:to="{name: 'RegistrationInfo'}") {{ $t('signup.loginButtonRegistrationTooltip') }}

</template>

<script>
import {
    phaseExpired,
    phaseEmailVerified,
    phaseBusy
} from '@/store/ux';

export default {
    name: 'login-button',

    props: {
        isAuthenticated: {  type: Boolean, required: true },
        disableTooltip: {   type: Boolean }
    },

    components: {
        DxPopover: () => import('devextreme-vue/popover')
        // DxTooltip: () => import('devextreme-vue/tooltip')
    },

    data: function () {
        return {
            busy: false,
            show: true,
            showPopover: false,
            showSessionExpired: false,
            showRegistrationInfo: false,
            waitInitialSession: false,
            popoverTarget: null,
            // position: 'bottom', // shows the little arrow
            position: {
                my: 'bottom', // does not calc arrow position
                offset: '-30 -30'
            },
            animationConfig: {
                show: {
                    type: 'pop',
                    from: {
                        scale: 0
                    },
                    to: {
                        scale: 1
                    }
                },
                hide: {
                    type: 'fade',
                    from: 1,
                    to: 0
                }
            }
        };
    },

    beforeMount: function () {
        if (this.$route?.name?.match(/InfoMessage|RegistrationInfo/) && this.disableTooltip) {
            // button inline registration info
            this.waitInitialSession = false;
        }
        if (this.$store.getters['ux/getLoginPhase'] === phaseBusy) {
            // wait for session if login is in process
            this.waitInitialSession = true;
        }
        this.update();
    },

    beforeDestroy: function () {
        this.popoverTarget = null;
    },

    watch: {
        isAuthenticated: function () {
            this.waitInitialSession = false;
            this.update();
        }
    },

    methods: {

        /**
         * Login by emitting `user-login` signal to the global bus.
         * The actual `onLogin()` method is in UserSessionMixin
         * and it will redirect the browser.
         */
        onClick: function () {
            this.busy = true;
            this.$store.commit('ux/setLoginPhase', phaseBusy);
            this.$eventBus.$emit('user-login');
        },

        update: function () {
            if (this.isAuthenticated) {
                this.busy = false;
                this.show = false;
                this.showRegistrationInfo = false;
                this.showSessionExpired = false;
                this.showPopover = false;
                this.popoverTarget = null;
            }
            else if (this.waitInitialSession) {
                this.busy = true;
                this.show = false;
            }
            else {
                this.busy = false;
                this.show = true;

                // no popup on InfoPages
                // NOTE: button can be hidden by route meta `no-login`
                if (this.$route?.name?.match(/InfoMessage|RegistrationInfo/)) {
                    this.showPopover = false;
                }
                else {
                    this.updateMessages();
                    this.$nextTick(() => {
                        if (!this.disableTooltip && this.showPopover) {
                            this.popoverTarget = this.$refs.loginButton || this.$el;
                        }
                    });
                }
            }
        },

        /**
         * Show popover and/or tooltip messages for the button.
         * NOTE: registration button in the navbar shows this message, except when it is not
         *       on the page, as on mobile. Hence the message can be shown by this login-button.
         */
        updateMessages: function () {
            const loginPhase = this.$store.getters['ux/getLoginPhase'];

            // session expired popup
            this.showSessionExpired = (loginPhase === phaseExpired);

            // anonymous registration info popup
            if (loginPhase < phaseEmailVerified) {
                if (this.$store.getters['ux/getShowRegistrationInfo']
                    && process.client
                    // $breakpoint-mobile: 576px; - co-dependency in Navigation media queries
                    && window.innerWidth && window.innerWidth < 576) {

                    // tooltip is shown delayed, to prevent it from flashing during load
                    _.debounce(() => {
                        this.showRegistrationInfo = true;
                    }, 1500, {trailing: true})();
                }
                else {
                    this.showRegistrationInfo = false;
                }
            }

            this.showPopover = (this.showSessionExpired || this.showRegistrationInfo);
        },

        /**
         * Dismiss session expired popover message.
         * If ux loginPhase is expired, set it to anonymous.
         */
        loginDismiss: function () {
            this.showSessionExpired = false;
            if (this.$store.getters['ux/getLoginPhase'] === phaseExpired) {
                this.$store.commit('ux/setLoginPhase', phaseEmailVerified);
            }
        }
    }
};
</script>

<style lang="scss">
/** .login-button style set in app.scss */

// transparent login-popover dx-overlay
// (requires devextreme v21.2 to set unique class via v-bind:wrapper-attr)
.dx-popover-wrapper.dx-overlay-wrapper.login-popover-wrapper {

    // z-index: 7000 !important; // should not overlap navbar language dropdown
    //                              although the tooltip popover SHOULD overlap navbar

    .dx-overlay-content {
        background: transparent;
        box-shadow: none;
        .dx-popover-arrow {
            display: none !important;
        }
    }
    .popover-message {
        display: inline-flex;
        flex-direction: column;
        max-width: 400px;
        font-size: 1rem;
        text-align: center;
        .silhouette-assistant {
            margin: -10px 0 -10px -10px; // dx-popover adds padding
            .bubble-wrapper {
                align-self: flex-start;
                min-height: 0;
                margin-left: 1rem;
                padding-top: 11%;
                padding-bottom: 11%;
                div {
                    margin-bottom: 1rem;
                }
            }
        }
        button {
            margin-top: 1rem;
        }
    }
}
</style>
